import { Grid, Box } from '@mui/material'
import { Card, CardHeader, SelectC, TextFieldC } from 'components'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { Yup, zipCodeMask } from 'utils'
import { useLoadStates, useLoadCities } from 'services/api/Generals'
import { useFetchCep } from 'modules/customers/hooks'

export const AddressSchema = Yup.object({
  state: Yup.object({
    id: Yup.string().required(),
    name: Yup.string().required()
  }).required(),
  city: Yup.object({
    id: Yup.string().required(),
    name: Yup.string().required()
  }).required(),
  number: Yup.string().required().max(10),
  district: Yup.string().max(20),
  complement: Yup.string().max(15),
  street: Yup.string().required().max(40),
  zipCode: Yup.string()
})

export type AddressType = {
  state: { id: string; name: string } | null
  city: { id: string; name: string } | null
  number: string
  district: string
  complement: string
  zipCode: string
  street: string
}

export const addressDefaultValues = {
  state: null,
  city: null,
  number: '',
  district: '',
  complement: '',
  zipCode: '',
  street: ''
}

export const AddressForm = () => {
  const { watch, setValue } = useFormContext()

  const selectedState = watch('address.state')
  const zipCode = watch('address.zipCode')

  const { data: states, isLoading: loadingStates } = useLoadStates({})
  const { data: cities, isLoading: loadingCities } = useLoadCities({
    stateId: selectedState?.id
  })

  const { data: cepData, isFetching: fetchingCep } = useFetchCep({
    cep: zipCode
  })

  useEffect(() => {
    setValue('address.city', null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState])

  useEffect(() => {
    if (cepData) {
      const state = states?.data?.find((s: any) => s.id === cepData.uf)
      if (state) {
        setValue('address.state', state)
      }
      // Só sobrescreve campos que estiverem vazios e quando o ViaCEP
      // retornar valor — evita apagar dados já preenchidos (ex.: vindos
      // da busca por CNPJ) em CEPs genéricos de cidade pequena.
      if (cepData.logradouro && !watch('address.street')) {
        setValue('address.street', cepData.logradouro)
      }
      if (cepData.bairro && !watch('address.district')) {
        setValue('address.district', cepData.bairro)
      }
      if (cepData.complemento && !watch('address.complement')) {
        setValue('address.complement', cepData.complemento)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cepData, states?.data, setValue])

  useEffect(() => {
    if (cepData && cities?.data) {
      const city = cities.data.find(
        (c: any) => c.name.toUpperCase() === cepData.localidade
      )
      if (city) {
        setValue('address.city', city)
      }
    }
  }, [cepData, cities?.data, setValue])

  return (
    <div>
      <Card
        sx={{
          visibility: { xs: 'hidden', sm: 'visible', md: 'visible' },
          padding: { xs: 0, sm: '24px', md: '24px' },
          maxWidth: 850
        }}
      >
        <Box
          sx={{
            visibility: { xs: 'visible', sm: 'visible', md: 'visible' }
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              visibility: 'visible'
            }}
          >
            <CardHeader title="Endereço" />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextFieldC
                  name="address.zipCode"
                  label="CEP"
                  normalize={(value) => zipCodeMask(value)}
                  loading={fetchingCep}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SelectC
                  name="address.state"
                  options={states?.data || []}
                  loading={loadingStates}
                  label="Estado"
                  getOptionLabel={(option: any) => option?.id}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SelectC
                  name="address.city"
                  options={cities?.data || []}
                  label="Cidade"
                  loading={loadingCities}
                  // disabled={!selectedState}
                  getOptionLabel={(option: any) => option?.name}
                />
              </Grid>
              <Grid item xs={12}>
                <TextFieldC name="address.street" label="Logradouro" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextFieldC name="address.number" label="Número" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextFieldC name="address.district" label="Bairro" />
              </Grid>
              <Grid item xs={12}>
                <TextFieldC name="address.complement" label="Complemento" />
              </Grid>
            </Grid>
          </div>
        </Box>
      </Card>
    </div>
  )
}
